/**
 * Push to talk over OpenAI Realtime (WebRTC). Hold Space, Alt+R or the button. Nothing connects,
 * and the microphone is not asked for, until the first press: a paid session is opened only for a
 * controller who talks. After that it reconnects on its own after a menu or player pause, and a
 * press after an idle disconnect reconnects it. While open it pings the server, which hangs up a
 * call that goes quiet and ends one that runs past the budget. The microphone stays live for a short tail after release so the
 * last word is not clipped, and each transmission is interpreted out of band so the Realtime
 * conversation never grows. What the model heard is resolved locally (voice-resolve.ts): a clearance that resolves
 * executes at once and the pilot reads it back, like a real frequency; anything else makes the
 * pilot ask to say again. `?radiodebug=1` logs Realtime events and interpretations.
 */
import { spokenCallsign, type PilotTransmission, type Clearance, type CommandResult, type Sim } from '@atc/sim';
import { readVoiceProposal } from './voice-proposal';
import { resolveHeardClearance, resolveSpokenCallsign, type ResolveContext } from './voice-resolve';
import { VoiceTurn } from './voice-turn';
import { attribution } from './attribution';

/**
 * WebRTC audio is still in flight when the key comes up; commit only after this tail. The session
 * logs showed whole words lost off the end of a transmission, so this is deliberately generous.
 */
export const TAIL_MS = 450;
/**
 * A press shorter than this carried no speech worth sending. Kept low: a clipped half-word is
 * still better than silently dropping a transmission the controller believes they made.
 */
const MIN_HOLD_MS = 150;
const MAX_HOLD_MS = 15000, RESPONSE_TIMEOUT_MS = 10000, IDLE_MS = 180000, PING_MS = 30000;
/** Must match the transcription model in voice/session.mjs. */
const TRANSCRIBE_MODEL = 'gpt-4o-mini-transcribe';
/** Server setup instructions are for whoever runs `pnpm dev`, not for players. */
const DEV = (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
const DEBUG = typeof location !== 'undefined' && new URLSearchParams(location.search).has('radiodebug');

export interface VoicePanelOptions {
  interpretation?: 'transcript' | 'direct';
  sendText?(text: string): Promise<CommandResult | null>;
  /** Raw speech-to-text for audit when legacy direct-audio interpretation is selected. */
  onTranscript?(text: string): string | void;
  onDirectResult?(traceId: string, result: CommandResult, details?: Record<string, unknown>): void;
  sendTransmission?(tx: PilotTransmission): CommandResult;
  onTransmit(active: boolean): void;
  onSession(id: string | null): void;
  /** Whether this server offers push to talk at all, once known. */
  onAvailability?(available: boolean): void;
  send(clearance: Clearance): CommandResult;
  sayAgain(callsign?: string): void;
  /** The aircraft you were last talking to, for a transmission with no callsign in it. */
  partner?(): string | null;
  /**
   * The aircraft picked on the radar. It biases the transcription, breaks a tie between callsigns
   * that read alike, and stands in for a callsign the transcript lost, so pressing a blip and
   * talking is the reliable way to reach one aircraft.
   */
  selected?(): string | null;
}

/** A browser's microphone refusal, in words a player can act on. */
export function micProblem(err: unknown): string {
  const name = err instanceof Error || (typeof DOMException !== 'undefined' && err instanceof DOMException) ? (err as Error).name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    const safari = typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    return safari
      ? 'Microphone blocked. Allow it in Safari > Settings for This Website, then hold to talk again. Typed clearances still work.'
      : 'Microphone blocked. Click the icon at the left of the address bar, allow the microphone, then hold to talk again. Typed clearances still work.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No microphone found. Plug one in and hold to talk again, or type your clearances.';
  if (name === 'NotReadableError') return 'Another app is using the microphone. Close it and hold to talk again, or type your clearances.';
  return err instanceof Error && err.message ? err.message : 'Microphone or radio unavailable.';
}

export function createVoicePanel(root: HTMLElement, sim: Sim, options: VoicePanelOptions) {
  root.className = 'voice-panel';
  root.innerHTML = `<button type="button" class="g-btn" data-talk disabled title="Your microphone audio is sent to OpenAI only while you transmit.">Hold to talk</button>
    <span class="tx-lamp" aria-hidden="true"></span><span class="mic-level" aria-hidden="true"><span></span></span>
    <span role="status" data-status>Checking radio availability…</span>
    <p data-transcript></p><p data-voice-result aria-live="polite"></p>`;
  const $ = <T extends HTMLElement = HTMLElement>(selector: string) => root.querySelector<T>(selector)!;
  const talk = $<HTMLButtonElement>('[data-talk]');
  const status = $('[data-status]'), transcript = $('[data-transcript]'), result = $('[data-voice-result]'), level = $('.mic-level span');
  let peer: RTCPeerConnection | null = null, channel: RTCDataChannel | null = null, stream: MediaStream | null = null, sessionId: string | null = null;
  let meterCtx: AudioContext | null = null, analyser: AnalyserNode | null = null, meterRaf = 0;
  /** `paused` blocks transmitting; `silent` (menu, player pause, unopened brief) also drops the session. */
  let destroyed = false, paused = false, silent = false, available = false, connecting = false;
  /** The key or button is down. */
  let held = false;
  /** The microphone track is live, including the tail after release. */
  let recording = false;
  let waiting = false, pendingItem: string | null = null, lastItem: string | null = null, promptSent = '';
  let generation = 0, recordAt = 0, connectedAt = 0;
  let directTraceId: string | null = null;
  let directResult: { result: CommandResult; details?: Record<string, unknown> } | null = null;
  let idle: ReturnType<typeof setTimeout> | null = null, sessionTimer: ReturnType<typeof setTimeout> | null = null;
  let ping: ReturnType<typeof setInterval> | null = null;
  /** The controller has pressed to talk at least once; only then does a resume reconnect by itself. */
  let used = false;
  const turn = new VoiceTurn(), seen = new Set<string>(), abort = new AbortController();

  const send = (event: object) => { if (channel?.readyState === 'open') channel.send(JSON.stringify(event)); };
  const isOpen = () => channel?.readyState === 'open';
  const headers = () => ({ 'content-type': 'application/json', 'x-atc-voice': '1', ...attribution(), ...(sessionId ? { 'x-atc-session': sessionId } : {}) });
  const setStatus = (text: string) => { status.textContent = text; };
  const showResult = (text: string, ok: boolean) => { result.textContent = text; result.classList.toggle('error', !ok); };
  const tracks = (on: boolean) => stream?.getAudioTracks().forEach((t) => { t.enabled = on; });
  const visibleFixes = () => [...sim.airport.fixes.values()].filter((f) => !f.hidden).map((f) => f.name);
  const runways = () => sim.airport.runways.flatMap((r) => r.ends.map((e) => e.name));
  const resolveContext = (): ResolveContext => ({ aircraft: sim.context().map((a) => ({ callsign: a.callsign, airline: a.airline })), fixes: visibleFixes(), telephony: sim.airport.xAtc.telephony, partner: options.partner?.() ?? undefined, selected: options.selected?.() ?? undefined });
  const finishDirect = (result: CommandResult, details?: Record<string, unknown>) => {
    if (directTraceId) options.onDirectResult?.(directTraceId, result, details);
    else directResult = { result, details };
  };

  function sync() {
    talk.disabled = paused || !available;
    root.classList.toggle('transmitting', recording);
    root.classList.toggle('interpreting', waiting);
  }

  function armIdle() {
    if (idle) clearTimeout(idle);
    idle = setTimeout(() => { if (recording || waiting) { armIdle(); return; } close(); setStatus('Radio disconnected after inactivity. Hold to talk to reconnect.'); }, IDLE_MS);
  }

  function close() {
    generation++; held = recording = waiting = connecting = false; turn.cancel(); pendingItem = lastItem = null; promptSent = '';
    if (idle) clearTimeout(idle); if (sessionTimer) clearTimeout(sessionTimer); idle = sessionTimer = null;
    if (ping) clearInterval(ping); ping = null;
    options.onTransmit(false);
    tracks(false); stream?.getTracks().forEach((t) => t.stop()); stream = null; channel?.close(); channel = null; peer?.close(); peer = null;
    if (meterRaf) cancelAnimationFrame(meterRaf); meterRaf = 0; analyser = null; void meterCtx?.close().catch(() => {}); meterCtx = null; level.style.width = '0';
    if (sessionId) void fetch('/api/voice/session', { method: 'DELETE', headers: headers(), keepalive: true }).catch(() => {});
    sessionId = null; connectedAt = 0; options.onSession(null); sync();
  }

  function startMeter() {
    if (!analyser || meterRaf) return;
    const data = new Uint8Array(analyser.fftSize);
    const tick = () => {
      if (!recording || !analyser) { level.style.width = '0'; meterRaf = 0; return; }
      analyser.getByteTimeDomainData(data);
      let peak = 0; for (const x of data) peak = Math.max(peak, Math.abs(x - 128));
      level.style.width = `${Math.min(100, (peak / 128) * 160)}%`;
      meterRaf = requestAnimationFrame(tick);
    };
    meterRaf = requestAnimationFrame(tick);
  }

  /** Bias transcription toward what is on frequency right now. */
  function updatePrompt() {
    if (!isOpen() || recording || waiting) return;
    const tel = sim.airport.xAtc.telephony;
    const traffic = sim.context().slice(0, 20);
    const calls = traffic.map((a) => spokenCallsign(a.callsign, a.airline, tel));
    // The aircraft picked on the radar is the one about to be spoken to, so name it first: the
    // transcription model weights the prompt, and this is where most misheard callsigns are lost.
    const picked = options.selected?.() ?? null;
    const ac = picked ? traffic.find((a) => a.callsign === picked) : undefined;
    const working = ac ? `The controller is working ${spokenCallsign(ac.callsign, ac.airline, tel)}. ` : '';
    const prompt = `Air traffic controller radio at ${sim.airport.icao}. ${working}Callsigns on frequency: ${calls.join(', ') || 'none'}. Fixes: ${visibleFixes().slice(0, 80).join(', ')}. Runways: ${runways().join(', ')}.`.slice(0, 1000);
    if (prompt === promptSent) return;
    promptSent = prompt;
    send({ type: 'session.update', session: { type: 'realtime', audio: { input: { transcription: { model: TRANSCRIBE_MODEL, language: 'en', prompt } } } } });
  }

  async function connectRadio() {
    if (sessionId || connecting || silent || !available || destroyed) return;
    connecting = true; sync(); const run = ++generation;
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      if (run !== generation || destroyed || silent) { media.getTracks().forEach((t) => t.stop()); return; }
      stream = media; tracks(false);
      try { meterCtx = new AudioContext(); analyser = meterCtx.createAnalyser(); analyser.fftSize = 512; meterCtx.createMediaStreamSource(media).connect(analyser); }
      catch { analyser = null; void meterCtx?.close().catch(() => {}); meterCtx = null; }
      peer = new RTCPeerConnection(); peer.addTrack(stream.getAudioTracks()[0]!, stream);
      // Text-only session: nothing OpenAI sends back may ever reach the speakers.
      peer.addEventListener('track', (e) => { (e as RTCTrackEvent).track.enabled = false; });
      channel = peer.createDataChannel('oai-events');
      channel.addEventListener('open', () => {
        if (run !== generation) return;
        connecting = false; sync(); armIdle(); updatePrompt();
        // The key was already down while connecting: everything said until now was not captured.
      if (held) { beginRecording(); setStatus('Radio ready — say it again from the callsign.'); }
      else setStatus(paused ? 'Radio connected.' : 'Radio ready. Hold Space or Alt+R and speak once the lamp lights.');
      });
      channel.addEventListener('message', (message) => { if (run === generation) onEvent(message.data); });
      peer.addEventListener('connectionstatechange', () => { if (run === generation && peer && ['failed', 'disconnected'].includes(peer.connectionState)) { close(); setStatus('Radio connection lost. Hold to talk to reconnect; typed play still works.'); } });
      const offer = await peer.createOffer(); await peer.setLocalDescription(offer);
      const response = await fetch('/api/voice/session', { method: 'POST', headers: headers(), body: JSON.stringify({ sdp: offer.sdp }), signal: AbortSignal.any([abort.signal, AbortSignal.timeout(25000)]) });
      const answer = await response.json();
      if (!response.ok) throw new Error(answer.error || 'Radio connection failed.');
      if (run !== generation || destroyed || silent) { void fetch('/api/voice/session', { method: 'DELETE', headers: { ...headers(), 'x-atc-session': answer.sessionId }, keepalive: true }); return; }
      sessionId = answer.sessionId; connectedAt = performance.now(); options.onSession(sessionId); sync();
      await peer.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
      sessionTimer = setTimeout(() => { close(); setStatus('Radio session limit reached. Hold to talk to reconnect.'); }, answer.maxSessionMinutes * 60000);
      ping = setInterval(() => { void heartbeat(run); }, PING_MS);
    } catch (err) { if (run === generation) { close(); setStatus(micProblem(err)); } }
  }

  /** Tells the server the call is still wanted; it answers 503 when the budget has run out. */
  async function heartbeat(run: number) {
    if (!sessionId || run !== generation) return;
    try {
      const r = await fetch('/api/voice/session/ping', { method: 'POST', headers: headers(), signal: AbortSignal.any([abort.signal, AbortSignal.timeout(10000)]) });
      if (r.ok || run !== generation) return;
      const body = await r.json().catch(() => ({})) as { error?: string };
      close();
      setStatus(r.status === 503 && body.error ? `${body.error} Type your clearances.` : 'Radio session ended. Hold to talk to reconnect.');
    } catch { /* a missed ping is harmless; the server allows for a few */ }
  }

  function onEvent(raw: unknown) {
    if (paused || destroyed) return;
    let event: any; try { event = JSON.parse(String(raw)); } catch { return; }
    if (!event || typeof event !== 'object') return;
    if (DEBUG && event.type !== 'response.function_call_arguments.delta') console.info('[voice]', event.type, event);
    switch (event.type) {
      case 'input_audio_buffer.committed':
        if (waiting && turn.id && !pendingItem && typeof event.item_id === 'string') { pendingItem = event.item_id; if (options.interpretation !== 'transcript') interpret(turn.id, event.item_id); }
        return;
      case 'conversation.item.input_audio_transcription.completed':
        if (typeof event.transcript === 'string' && (event.item_id === pendingItem || event.item_id === lastItem)) transcript.textContent = `“${event.transcript.trim()}”`;
        if (options.interpretation !== 'transcript' && (event.item_id === pendingItem || event.item_id === lastItem) && typeof event.transcript === 'string') {
          directTraceId = options.onTranscript?.(event.transcript.trim()) ?? null;
          if (directTraceId && directResult) { options.onDirectResult?.(directTraceId, directResult.result, directResult.details); directResult = null; }
        }
        if (options.interpretation === 'transcript' && waiting && event.item_id === pendingItem && typeof event.transcript === 'string') {
          endTurn();
          void options.sendText?.(event.transcript).then((r) => { if (r && !destroyed && !paused) { showResult(r.message, r.ok); setStatus('Radio ready.'); } });
        }
        return;
      case 'response.created':
        if (waiting && event.response) turn.created(event.response);
        return;
      case 'error':
        if (waiting) { endTurn(); setStatus(`Radio could not interpret that transmission${event.error?.message ? ` (${event.error.message})` : ''}. Try again or type the clearance.`); }
        return;
      case 'response.done':
        if (waiting && event.response && turn.matches(event.response)) handleResponse(event);
    }
  }

  function interpret(turnId: string, itemId: string) {
    const tel = sim.airport.xAtc.telephony;
    const traffic = sim.context().map((a) => ({ spoken: spokenCallsign(a.callsign, a.airline, tel), callsign: a.callsign, kind: a.kind, phase: a.phase, runway: a.approachRunway ?? a.departureRunway }));
    const partner = options.partner?.() ?? null;
    const context = {
      airport: sim.airport.icao, runways: runways(), fixes: visibleFixes(), traffic, pilots: traffic.map((a) => sim.pilotContext(a.callsign)),
      // Mid-conversation a controller drops the callsign; this is who to assume, when none is spoken.
      conversationPartner: partner ? { callsign: partner, spoken: traffic.find((a) => a.callsign === partner)?.spoken ?? partner } : null,
    };
    send({ type: 'response.create', response: { conversation: 'none', output_modalities: ['text'], tool_choice: 'required', metadata: { atc_turn: turnId }, input: [
      { type: 'message', role: 'user', content: [{ type: 'input_text', text: `Game context for the controller transmission that follows: ${JSON.stringify(context)}` }] },
      { type: 'item_reference', id: itemId },
    ] } });
  }

  function endTurn() {
    waiting = false; turn.cancel();
    if (pendingItem) lastItem = pendingItem;
    pendingItem = null; armIdle(); sync();
  }

  function handleResponse(event: any) {
    const completed = event.response.status === 'completed';
    const proposal = readVoiceProposal(event);
    endTurn();
    if (proposal) { if (seen.has(proposal.callId)) return; seen.add(proposal.callId); if (seen.size > 100) seen.delete(seen.values().next().value!); }
    setStatus('Radio ready.');
    if (!completed) { setStatus('Radio could not interpret that transmission. Try again or type the clearance.'); return; }
    const ctx = resolveContext();
    if (!proposal || proposal.tool === 'request_repeat') {
      const callsign = (proposal?.tool === 'request_repeat' && proposal.spokenCallsign ? resolveSpokenCallsign(proposal.spokenCallsign, ctx).callsign : null) ?? ctx.selected ?? ctx.partner ?? null;
      options.sayAgain(callsign ?? undefined);
      showResult(`Not understood${callsign ? ` for ${callsign}` : ''}; the pilot asked you to say again.`, false);
      finishDirect({ ok: false, message: 'Say again.' }, { source: 'direct', resolution: 'clarification' });
      return;
    }
    if (proposal.tool === 'pilot_query') {
      const args = proposal.args as { spokenCallsign?: string; subject?: string };
      const callsign = typeof args.spokenCallsign === 'string' ? resolveSpokenCallsign(args.spokenCallsign, ctx).callsign : ctx.selected ?? null;
      if (!callsign) { options.sayAgain(); finishDirect({ ok: false, message: 'Say again.' }, { source: 'direct', resolution: 'unknown' }); return; }
      const r = options.sendTransmission?.({ callsign, messages: [{ kind: 'query', subject: args.subject as any }] });
      if (r) { showResult(r.message, r.ok); finishDirect(r, { source: 'direct', callsign }); } return;
    }
    const resolved = resolveHeardClearance(proposal.args, ctx);
    if (DEBUG) console.info('[voice] heard', proposal.args, '→', resolved);
    if (!resolved.ok) { options.sayAgain(resolved.callsign); const failed = { ok: false, message: resolved.reason }; showResult(`${resolved.reason} The pilot asked you to say again.`, false); finishDirect(failed, { source: 'direct', callsign: resolved.callsign, resolution: 'clarification' }); return; }
    const outcome = options.send(resolved.clearance);
    finishDirect(outcome, { source: 'direct', callsign: resolved.clearance.callsign, ...(resolved.assumed ? { assumed: true } : {}) });
    showResult(`${resolved.correction ? 'Correction. ' : ''}${resolved.assumed ? `Callsign not heard; sent to the selected ${resolved.clearance.callsign}. ` : ''}${outcome.message}`, outcome.ok);
  }

  function beginRecording() {
    if (recording || paused || !isOpen()) return;
    if (waiting) { send({ type: 'response.cancel' }); endTurn(); } // a new transmission replaces one still being interpreted
    if (lastItem) { send({ type: 'conversation.item.delete', item_id: lastItem }); lastItem = null; }
    send({ type: 'input_audio_buffer.clear' });
    recording = true; directTraceId = null; directResult = null; recordAt = performance.now(); transcript.textContent = ''; tracks(true);
    const target = options.selected?.() ?? null;
    options.onTransmit(true); setStatus(target ? `Transmitting to ${target} — speak now.` : 'Transmitting — speak now.'); startMeter(); armIdle(); sync();
    const run = generation, at = recordAt;
    setTimeout(() => { if (recording && held && run === generation && recordAt === at) { release(); setStatus('Transmission cut at 15 seconds.'); } }, MAX_HOLD_MS);
  }

  function stopTrack() { recording = false; tracks(false); options.onTransmit(false); sync(); }

  function press() {
    if (held || paused || destroyed) return;
    if (!available) { setStatus('Push to talk is not offered on this server. Type the clearance.'); return; }
    held = true; used = true;
    if (recording) return; // pressed again during the tail: the same transmission continues
    // Bias transcription now, not at the next tick: the aircraft may have been picked a moment ago.
    updatePrompt();
    if (isOpen()) beginRecording();
    else { setStatus('Connecting radio… wait for the lamp before you speak.'); void connectRadio(); }
  }

  function release() {
    if (!held) return;
    held = false;
    if (!recording) return;
    if (performance.now() - recordAt < MIN_HOLD_MS) { stopTrack(); send({ type: 'input_audio_buffer.clear' }); setStatus('Too short to send. Hold the key down for the whole transmission.'); return; }
    const run = generation, at = recordAt;
    setTimeout(() => {
      if (run !== generation || recordAt !== at || !recording || held) return;
      stopTrack();
      const turnId = turn.begin(); waiting = true; pendingItem = null; sync();
      setStatus('Interpreting…');
      send({ type: 'input_audio_buffer.commit' });
      setTimeout(() => { if (waiting && run === generation && turn.id === turnId) { send({ type: 'response.cancel' }); endTurn(); setStatus('No answer from the radio service. Try again or type the clearance.'); } }, RESPONSE_TIMEOUT_MS);
    }, TAIL_MS);
  }

  // --- Keys: Space (outside text fields, or in an empty/prefilled command box) and Alt+R. ---
  const spaceIsTalk = (e: KeyboardEvent) => {
    if (e.code !== 'Space' || e.ctrlKey || e.metaKey || e.altKey) return false;
    const t = e.target;
    if (t instanceof HTMLInputElement && t.classList.contains('rs-input')) return /^([A-Z0-9]{2,8} )?$/i.test(t.value);
    return !(t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)));
  };
  const keyDown = (e: KeyboardEvent) => {
    if (paused || destroyed || !available || !((e.altKey && e.code === 'KeyR') || spaceIsTalk(e))) return;
    e.preventDefault(); e.stopPropagation();
    if (!e.repeat) press();
  };
  const keyUp = (e: KeyboardEvent) => {
    if ((e.code !== 'KeyR' && e.code !== 'Space') || !held) return;
    e.preventDefault(); e.stopPropagation(); release();
  };
  const blur = () => {
    if (!held && !recording) return;
    held = false;
    if (recording) { stopTrack(); send({ type: 'input_audio_buffer.clear' }); setStatus('Transmission cancelled when the window lost focus.'); }
  };
  window.addEventListener('keydown', keyDown, true); window.addEventListener('keyup', keyUp, true); window.addEventListener('blur', blur);
  talk.addEventListener('pointerdown', (e) => { e.preventDefault(); talk.setPointerCapture(e.pointerId); press(); });
  talk.addEventListener('pointerup', release); talk.addEventListener('pointercancel', release);
  talk.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.repeat) { e.preventDefault(); press(); } });
  talk.addEventListener('keyup', (e) => { if (e.key === 'Enter') { e.preventDefault(); release(); } });

  // A proxy error (service not started) is not the same as a service with voice switched off.
  // Players only need to know typing still works; the fix is for whoever runs the server.
  const notRunning = DEV ? 'Voice service is not running. Start it with pnpm dev:voice in a second terminal, then reload. Typed play works meanwhile.' : 'Push to talk is unavailable right now. Type your clearances.';
  const unavailable = () => { available = false; options.onAvailability?.(false); sync(); };
  void fetch('/api/voice/capabilities', { signal: abort.signal }).then(async (r) => {
    if (destroyed) return;
    if (!r.ok) { setStatus(notRunning); unavailable(); return; }
    const c = await r.json();
    available = c.available === true;
    options.onAvailability?.(available);
    setStatus(available ? 'Push to talk ready. Hold Space or Alt+R to talk; the first press asks for your microphone.' : DEV ? 'OpenAI radio is off: set OPENAI_API_KEY and ATC_OPENAI_VOICE_ENABLED=true in .env, then restart pnpm dev:voice.' : 'Push to talk is not offered on this server. Type your clearances.');
    sync();
  }).catch(() => { if (!destroyed) { setStatus(notRunning); unavailable(); } });
  const ticker = setInterval(() => { if (!destroyed) updatePrompt(); }, 5000);
  const leave = () => close(); window.addEventListener('pagehide', leave);
  return {
    /**
     * `v` stops transmissions; `drop` also closes the session so an idle menu costs nothing. A
     * lesson's pause between steps is a beat inside play and keeps the radio connected.
     */
    setPaused(v: boolean, drop = v) {
      const was = paused;
      paused = v; silent = v && drop;
      if (silent) { if (sessionId || connecting) { close(); setStatus('Radio off while paused. It reconnects when play resumes.'); } }
      else if (was && !v) {
        // Back in play: reconnect anything a pause or the idle timer dropped, without a press.
        if (sessionId) setStatus('Radio ready.');
        else if (available && used) { setStatus('Connecting radio…'); void connectRadio(); }
        else if (available) setStatus('Push to talk ready. Hold Space or Alt+R to talk.');
      }
      sync();
    },
    destroy() {
      destroyed = true; clearInterval(ticker); abort.abort(); close();
      window.removeEventListener('keydown', keyDown, true); window.removeEventListener('keyup', keyUp, true); window.removeEventListener('blur', blur); window.removeEventListener('pagehide', leave);
      root.replaceChildren();
    },
  };
}
